import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { FaShieldAlt, FaLeaf, FaVolumeDown, FaCheckCircle, FaQuoteLeft, FaStar, FaArrowRight } from 'react-icons/fa';
import { RiShieldCheckFill } from 'react-icons/ri';
import { HiOutlineClock } from 'react-icons/hi';
import win1 from '../assets/images/win1.jpg';
import impact from '../assets/images/impact.jpg';
import hurricane from '../assets/images/hurr.jpg';
import modern from '../assets/images/mod.jpg';
import sliding from '../assets/images/slide.jpg';
import profile1 from '../assets/images/1.jpg';
import profile2 from '../assets/images/2.jpg';
import profile3 from '../assets/images/3.jpg';
import profile4 from '../assets/images/4.jpg';
const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Handle scroll events for animations
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setIsVisible(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      title: 'Hurricane Protection',
      description: 'Our impact-resistant windows and doors provide superior protection against severe weather.',
      icon: <FaShieldAlt className="w-6 h-6 text-primary-600" />,
      link: '/advantages#hurricane-protection'
    },
    {
      title: 'Energy Efficiency',
      description: 'Save on energy costs with our high-performance, insulated products.',
      icon: <FaLeaf className="w-6 h-6 text-primary-600" />,
      link: '/advantages#energy-efficiency'
    },
    {
      title: 'Noise Reduction',
      description: 'Enjoy a quieter home with our sound-dampening technology.',
      icon: <FaVolumeDown className="w-6 h-6 text-primary-600" />,
      link: '/advantages#noise-reduction'
    }
  ];

  const benefits = [
    'Miami-Dade County Approved Products',
    'Professional Installation Team',
    'Lifetime Warranty',
    'Energy Star Certified',
    'Flexible Financing Options'
  ];

  const testimonials = [
    {
      text: "The quality of their impact windows is outstanding. Installation was quick and professional.",
      name: "John Martinez",
      location: "Miami, FL",
      rating: 5,
      image: profile1
    },
    {
      text: "Best investment we've made for our home. The energy savings are remarkable!",
      name: "Sarah Thompson",
      location: "Fort Lauderdale, FL",
      rating: 5,
      image: profile4
    },
    {
      text: "Excellent service from start to finish. Very knowledgeable and professional team.",
      name: "Michael Rodriguez",
      location: "Palm Beach, FL",
      rating: 5,
      image: profile3
    }
  ];

  const process = [
    {
      title: 'Free Consultation',
      description: 'Schedule a no-obligation consultation with our experts'
    },
    {
      title: 'Custom Design',
      description: 'Get a personalized plan tailored to your home'
    },
    {
      title: 'Professional Installation',
      description: 'Expert installation by our certified team'
    },
    {
      title: 'Lifetime Support',
      description: 'Enjoy peace of mind with our lifetime warranty'
    }
  ];

  const gallery = [
    {
      title: 'Modern Impact Windows',
      location: 'Miami Beach Residence',
      image: modern
    },
    {
      title: 'Hurricane Impact Doors',
      location: 'Boca Raton Villa',
      image: hurricane
    },
    {
      title: 'Sliding Glass Doors',
      location: 'Palm Beach Estate',
      image: sliding
    }
  ];

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };
  
  const slideIn = {
    initial: { opacity: 0, x: -30 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.5 }
  };

  return (
    <div className="bg-gray-50">
      {/* Hero Section with Architectural Background */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-16">
        <div className="absolute inset-0 z-0">
          <img 
            src={win1}
            alt="Architectural Windows"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={fadeIn.initial}
              animate={fadeIn.animate}
              transition={fadeIn.transition}
              className="text-white"
            >
              <span className="inline-block px-4 py-1 bg-primary-500/20 backdrop-blur-sm text-primary-300 rounded-full text-sm font-medium mb-6">
                PREMIUM QUALITY WINDOWS & DOORS
              </span>
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-300 via-primary-400 to-primary-300">
                  Transform Your
                </span>
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-primary-300 via-primary-400 to-primary-300">
                  Living Space
                </span>
              </h1>
              <div className="w-24 h-1.5 bg-gradient-to-r from-primary-500 to-primary-400 mb-8"></div>
              <p className="text-xl md:text-2xl mb-8 text-gray-100 max-w-xl leading-relaxed">
                Elevate your home's beauty and security with our premium impact-resistant windows and doors. 
                <span className="text-primary-300"> Crafted for Florida's unique climate.</span>
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/get-estimate"
                  className="bg-primary-600 text-white px-8 py-4 rounded-md text-lg font-semibold hover:bg-primary-700 transition duration-300 flex items-center justify-center group"
                >
                  Get a Free Quote 
                  <FaArrowRight className="ml-2 transform group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/products"
                  className="bg-white/10 backdrop-blur-sm border-2 border-white/20 text-white px-8 py-4 rounded-md text-lg font-semibold hover:bg-white/20 transition duration-300 flex items-center justify-center"
                >
                  View Products
                </Link>
              </div>
              
              <div className="flex items-center mt-12 bg-black/30 backdrop-blur-sm p-4 rounded-lg border border-white/10">
                <div className="flex -space-x-4">
                  <img 
                    src={profile1}
                    alt="Satisfied Customer" 
                    className="w-12 h-12 rounded-full border-2 border-primary-500 object-cover"
                  />
                  <img 
                    src={profile2}
                    alt="Satisfied Customer" 
                    className="w-12 h-12 rounded-full border-2 border-primary-500 object-cover"
                  />
                  <img 
                    src={profile3}
                    alt="Satisfied Customer" 
                    className="w-12 h-12 rounded-full border-2 border-primary-500 object-cover"
                  />
                  <img 
                    src={profile4}
                    alt="Satisfied Customer" 
                    className="w-12 h-12 rounded-full border-2 border-primary-500 object-cover"
                  />
                </div>
                <div className="ml-4">
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <FaStar key={i} className="text-yellow-400 w-5 h-5" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-200 mt-1">Over 500+ satisfied customers</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="hidden lg:block"
            >
              <div className="bg-white/10 backdrop-blur-md p-8 rounded-lg shadow-xl border border-white/20">
                <h3 className="text-2xl font-bold text-white mb-4">Get Your Instant Quote</h3>
                <p className="text-gray-200 mb-6">Fill out the form below for a free consultation</p>
                
                <form className="space-y-4">
                  <div>
                    <input
                      type="text"
                      placeholder="Full Name"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-white placeholder-gray-300"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      placeholder="Email Address"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-white placeholder-gray-300"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      placeholder="Phone Number"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-white placeholder-gray-300"
                    />
                  </div>
                  <div>
                    <select className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-white">
                      <option className="bg-gray-900">Select Service</option>
                      <option className="bg-gray-900">Impact Windows</option>
                      <option className="bg-gray-900">Impact Doors</option>
                      <option className="bg-gray-900">Both Windows & Doors</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-primary-600 text-white py-3 rounded-md font-semibold hover:bg-primary-700 transition duration-300"
                  >
                    Submit Request
                  </button>
                </form>
                
                <div className="mt-6 text-center text-sm text-gray-300">
                  By submitting, you agree to our terms and privacy policy
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900 to-transparent h-32 z-0"></div>
      </section>

      {/* Trusted by Section */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <p className="text-gray-600 font-medium mb-4 md:mb-0">Trusted by homeowners across Florida:</p>
            <div className="flex flex-wrap justify-center gap-8 md:gap-12">
              {['Miami-Dade', 'Broward County', 'Palm Beach', 'Tampa Bay', 'Orlando'].map((location) => (
                <div key={location} className="text-gray-500 font-semibold">{location}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section with Icons */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={fadeIn.initial}
            whileInView={fadeIn.animate}
            viewport={{ once: true }}
            transition={fadeIn.transition}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">OUR ADVANTAGES</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Impact Windows & Doors?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide premium hurricane protection solutions with exceptional quality, design, and service
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={fadeIn.initial}
                whileInView={fadeIn.animate}
                viewport={{ once: true }}
                transition={{ ...fadeIn.transition, delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-lg shadow-sm hover:shadow-md transition duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-primary-100 rounded-lg flex items-center justify-center mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <Link to={feature.link} className="text-primary-600 font-medium flex items-center hover:text-primary-700">
                  Learn more <FaArrowRight className="ml-2 text-sm" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Showcase Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:w-1/2"
            >
              <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">PREMIUM QUALITY</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Exceptional Quality and Engineering Excellence
              </h2>
              <p className="text-gray-600 mb-8">
                Our impact windows and doors are engineered to withstand hurricane-force winds while enhancing your home's appearance. Using advanced materials and precise manufacturing, we deliver products that exceed industry standards for safety and performance.
              </p>
              
              <div className="space-y-4 mb-8">
                {benefits.map((benefit, index) => (
                  <motion.div 
                    key={benefit}
                    initial={slideIn.initial}
                    whileInView={slideIn.animate}
                    viewport={{ once: true }}
                    transition={{ ...slideIn.transition, delay: index * 0.1 }}
                    className="flex items-start"
                  >
                    <FaCheckCircle className="text-primary-500 mt-1 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/products"
                  className="bg-primary-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-primary-700 transition duration-300 text-center"
                >
                  Explore Products
                </Link>
                <Link
                  to="/advantages"
                  className="border border-gray-300 text-gray-700 px-6 py-3 rounded-md font-semibold hover:bg-gray-100 transition duration-300 text-center"
                >
                  View Advantages
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:w-1/2"
            >
              <div className="relative">
                <img 
                  src={impact} 
                  alt="Impact Window Showcase" 
                  className="rounded-lg shadow-xl w-full"
                />
                <div className="absolute bottom-0 right-0 transform translate-y-4 md:translate-y-6 -translate-x-4 md:-translate-x-6 bg-white p-3 md:p-4 rounded-lg shadow-lg">
                  <div className="flex items-center gap-2">
                    <RiShieldCheckFill className="text-primary-500 text-lg md:text-xl" />
                    <span className="font-bold text-gray-900 text-sm md:text-base">Miami-Dade Approved</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Special Offer Banner */}
      <motion.section
        initial={fadeIn.initial}
        whileInView={fadeIn.animate}
        viewport={{ once: true }}
        transition={fadeIn.transition}
        className="bg-primary-600 py-16 relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500 rounded-full opacity-20 -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary-500 rounded-full opacity-20 -ml-32 -mb-32"></div>
      
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-white lg:max-w-xl">
              <span className="inline-block px-4 py-1 bg-white text-primary-600 rounded-full text-sm font-medium mb-4">LIMITED TIME OFFER</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Special Spring Promotion:<br />75% Off Installation!
              </h2>
              <p className="text-xl mb-8 text-gray-100">
                Plus, receive complimentary hurricane shutters with any whole-house window replacement. Offer valid until April 30th.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-lg w-full lg:w-auto">
              <div className="flex items-center gap-3 mb-6">
                <HiOutlineClock className="text-primary-600 text-2xl" />
                <div>
                  <p className="text-gray-900 font-bold">Limited Time Only</p>
                  <p className="text-gray-500">Offer expires in 41 days</p>
                </div>
              </div>
              <Link
                to="/promotions"
                className="block bg-primary-600 text-white px-8 py-4 rounded-md text-lg font-semibold hover:bg-primary-700 transition duration-300 text-center"
              >
                Claim Your 75% Off Now
              </Link>
              <p className="text-sm text-gray-500 mt-4 text-center">No obligation estimate & consultation</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={fadeIn.initial}
            whileInView={fadeIn.animate}
            viewport={{ once: true }}
            transition={fadeIn.transition}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">OUR PROCESS</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple 4-Step Installation Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From consultation to final installation, we handle everything with expert care
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={step.title}
                initial={fadeIn.initial}
                whileInView={fadeIn.animate}
                viewport={{ once: true }}
                transition={{ ...fadeIn.transition, delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-gray-50 p-8 rounded-lg border border-gray-100 h-full flex">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold text-lg mr-4">
                    {index + 1}
                  </div>
                  {index < process.length - 1 && (
                    <div className="absolute top-1/2 left-full w-8 h-0.5 bg-gray-300 -translate-y-1/2 hidden lg:block"></div>
                  )}
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/advantages"
              className="text-primary-600 font-semibold hover:text-primary-700 transition duration-300 inline-flex items-center"
            >
              Learn more about our process <FaArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery Preview Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={fadeIn.initial}
            whileInView={fadeIn.animate}
            viewport={{ once: true }}
            transition={fadeIn.transition}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">INSPIRATION GALLERY</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              See Our Work in Action
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Browse our gallery of completed projects across Florida
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {gallery.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-lg"
              >
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="w-full h-80 object-cover transition duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition duration-300 flex items-end p-6">
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-200 text-sm">{item.location}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/products"
              className="bg-white border border-gray-300 text-gray-900 px-8 py-3 rounded-md font-semibold hover:bg-gray-50 transition duration-300 inline-block"
            >
              View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <FaQuoteLeft className="text-primary-600 text-9xl absolute top-10 left-10" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={fadeIn.initial}
            whileInView={fadeIn.animate}
            viewport={{ once: true }}
            transition={fadeIn.transition}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">TESTIMONIALS</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're proud to have helped thousands of Florida homeowners protect their properties
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 bg-gray-50 p-8 rounded-lg">
              <div className="mb-8">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                    {Math.floor(testimonials.reduce((acc, curr) => acc + curr.rating, 0) / testimonials.length * 10) / 10}
                  </div>
                  <div>
                    <div className="text-gray-900 font-bold text-lg">Excellent</div>
                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <FaStar key={i} className="text-yellow-400" />
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <FaCheckCircle className="text-green-500" />
                    <div className="text-gray-700">Over 500+ happy customers</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <FaCheckCircle className="text-green-500" />
                    <div className="text-gray-700">Fully licensed and insured</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <FaCheckCircle className="text-green-500" />
                    <div className="text-gray-700">10-year comprehensive warranty</div>
                  </div>
                </div>
              </div>
              
              <Link
                to="/testimonials"
                className="text-primary-600 font-semibold hover:text-primary-700 transition duration-300 inline-flex items-center"
              >
                Read all reviews <FaArrowRight className="ml-2" />
              </Link>
            </div>
            
            <div className="lg:col-span-2 relative">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="bg-white p-8 rounded-lg shadow-md border border-gray-100 h-full"
              >
                <FaQuoteLeft className="text-primary-200 text-4xl mb-4" />
                <p className="text-gray-700 text-lg mb-6 italic">
                  "{testimonials[currentSlide].text}"
                </p>
                <div className="flex items-center">
                  <img 
                    src={testimonials[currentSlide].image} 
                    alt={testimonials[currentSlide].name}
                    className="w-14 h-14 rounded-full object-cover mr-4"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">{testimonials[currentSlide].name}</div>
                    <div className="text-gray-500">{testimonials[currentSlide].location}</div>
                    <div className="flex items-center mt-1">
                      {[...Array(testimonials[currentSlide].rating)].map((_, i) => (
                        <FaStar key={i} className="text-yellow-400 text-sm" />
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
              
              <div className="flex justify-center mt-6 gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full ${
                      index === currentSlide ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Awards Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={fadeIn.initial}
            whileInView={fadeIn.animate}
            viewport={{ once: true }}
            transition={fadeIn.transition}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">RECOGNITION & AWARDS</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Award-Winning Service Excellence
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Recognized for our commitment to quality, service, and customer satisfaction
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="flex flex-col items-center text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 group cursor-pointer"
            >
              <div className="w-16 h-16 mb-4 transform group-hover:scale-110 transition-transform duration-300">
                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-primary-600">
                  <circle cx="32" cy="32" r="30" stroke="currentColor" strokeWidth="2" className="group-hover:stroke-primary-700"/>
                  <path d="M32 12L36.4 25.28L50 25.28L39.2 33.44L43.6 46.72L32 38.56L20.4 46.72L24.8 33.44L14 25.28L27.6 25.28L32 12Z" fill="currentColor" className="group-hover:fill-primary-700"/>
                  <text x="32" y="58" textAnchor="middle" fontSize="14" fill="currentColor" className="font-bold group-hover:fill-primary-700">700+</text>
                </svg>
              </div>
              <div className="flex items-center mb-2 transform group-hover:scale-110 transition-transform duration-300">
                {[1,2,3,4,5].map((_, i) => (
                  <FaStar key={i} className="text-yellow-400 w-4 h-4" />
                ))}
              </div>
              <div className="text-gray-900 font-semibold group-hover:text-primary-700 transition-colors duration-300">Google Reviews</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="flex flex-col items-center text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 group cursor-pointer"
            >
              <div className="w-16 h-16 mb-4 transform group-hover:scale-110 transition-transform duration-300">
                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-primary-600">
                  <path d="M32 4L60 16V48L32 60L4 48V16L32 4Z" stroke="currentColor" strokeWidth="2" className="group-hover:stroke-primary-700"/>
                  <text x="32" y="38" textAnchor="middle" fontSize="24" fill="currentColor" className="font-bold group-hover:fill-primary-700">A+</text>
                </svg>
              </div>
              <div className="text-gray-900 font-semibold group-hover:text-primary-700 transition-colors duration-300">BBB Rating</div>
              <div className="text-gray-600 text-sm group-hover:text-primary-600 transition-colors duration-300">Accredited Business</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="flex flex-col items-center text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 group cursor-pointer"
            >
              <div className="w-16 h-16 mb-4 transform group-hover:scale-110 transition-transform duration-300">
                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-primary-600">
                  <path d="M32 4L56 28L32 52L8 28L32 4Z" stroke="currentColor" strokeWidth="2" className="group-hover:stroke-primary-700"/>
                  <path d="M32 16L44 28L32 40L20 28L32 16Z" fill="currentColor" className="group-hover:fill-primary-700"/>
                  <path d="M26 46H38M24 50H40" stroke="currentColor" strokeWidth="2" className="group-hover:stroke-primary-700"/>
                </svg>
              </div>
              <div className="text-gray-900 font-semibold group-hover:text-primary-700 transition-colors duration-300">Super Service</div>
              <div className="text-gray-600 text-sm group-hover:text-primary-600 transition-colors duration-300">Award Winner</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="flex flex-col items-center text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 group cursor-pointer"
            >
              <div className="w-16 h-16 mb-4 transform group-hover:scale-110 transition-transform duration-300">
                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-primary-600">
                  <path d="M32 8L56 20V44L32 56L8 44V20L32 8Z" stroke="currentColor" strokeWidth="2" className="group-hover:stroke-primary-700"/>
                  <path d="M32 16L48 24V40L32 48L16 40V24L32 16Z" stroke="currentColor" strokeWidth="2" className="group-hover:stroke-primary-700"/>
                  <path d="M32 24L40 28V36L32 40L24 36V28L32 24Z" fill="currentColor" className="group-hover:fill-primary-700"/>
                  <path d="M20 16L44 48M44 16L20 48" stroke="currentColor" strokeWidth="2" className="group-hover:stroke-primary-700"/>
                </svg>
              </div>
              <div className="text-gray-900 font-semibold group-hover:text-primary-700 transition-colors duration-300">Energy Savings</div>
              <div className="text-gray-600 text-sm group-hover:text-primary-600 transition-colors duration-300">Energy Star Partner</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={fadeIn.initial}
            whileInView={fadeIn.animate}
            viewport={{ once: true }}
            transition={fadeIn.transition}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">FREQUENTLY ASKED QUESTIONS</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Common Questions About Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find answers to the most frequently asked questions about our windows, doors, and installation process
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">How long does installation typically take?</h3>
                <p className="text-gray-600">Most residential installations are completed within 1-2 days, depending on the project scope. Our team works efficiently while ensuring proper installation.</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Are your windows hurricane-rated?</h3>
                <p className="text-gray-600">Yes, all our windows are Miami-Dade County approved and exceed Florida's stringent hurricane protection requirements.</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Do you offer financing options?</h3>
                <p className="text-gray-600">Yes, we offer flexible financing options with competitive rates and terms to make your project more affordable.</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">What warranty do you offer?</h3>
                <p className="text-gray-600">We provide a comprehensive lifetime warranty on our products and a 10-year warranty on installation labor.</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">How much can I save on energy costs?</h3>
                <p className="text-gray-600">Our energy-efficient windows can help reduce your energy bills by up to 30%. The exact savings depend on your current windows and home configuration.</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Do you handle permits and inspections?</h3>
                <p className="text-gray-600">Yes, we handle all necessary permits and coordinate required inspections as part of our full-service approach.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="/pattern-bg.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-1 bg-primary-500 text-white rounded-full text-sm font-medium mb-4">GET STARTED TODAY</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to Upgrade Your Home with Premium Impact Windows & Doors?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Schedule your free in-home consultation and get a personalized quote today. Our experts are ready to help you find the perfect solution for your home.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/get-estimate"
                  className="bg-primary-600 text-white px-8 py-4 rounded-md text-lg font-semibold hover:bg-primary-700 transition duration-300 text-center"
                >
                  Schedule Consultation
                </Link>
                <Link
                  to="/products"
                  className="bg-white text-gray-900 px-8 py-4 rounded-md text-lg font-semibold hover:bg-gray-100 transition duration-300 text-center"
                >
                  View Our Products
                </Link>
              </div>
              
              <div className="mt-8">
                <p className="text-gray-400 flex items-center">
                  <FaCheckCircle className="text-primary-500 mr-2" /> 
                  No obligation quotes
                </p>
                <p className="text-gray-400 flex items-center mt-2">
                  <FaCheckCircle className="text-primary-500 mr-2" /> 
                  Flexible financing options available
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white p-8 rounded-lg shadow-xl"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Us</h3>
              <p className="text-gray-600 mb-8">
                Ready to upgrade your home with premium impact windows and doors? Contact us today to schedule a free consultation and get a personalized quote.
              </p>
              <form className="space-y-4">
                <div>
                  <input
                    type="text"
                    placeholder="Full Name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    placeholder="Email Address"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <textarea
                    placeholder="Message"
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-primary-600 text-white py-3 rounded-md font-semibold hover:bg-primary-700 transition duration-300"
                >
                  Send Message
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;